// Bio panel: fixed right-side card with Personality / Relationships / State
// tabs and an always-visible close button. Relationships use meter bars:
// bipolar scores (-10..10) fill from center, unipolar (0..10) from left.
import { useState } from 'react';
import { hasHistory, type UrgeInfo } from '../sim/relationships.js';
import type { Memory, Npc, Relationship, SleepState } from '../types.js';

export const NATURE_LABELS: Array<[key: keyof Npc['nature'], low: string, high: string]> = [
  ['sociability', 'solitary', 'highly social'],
  ['agreeableness', 'combative', 'accommodating'],
  ['emotionality', 'steady', 'intense'],
  ['caution', 'fearless', 'cautious'],
  ['discipline', 'impulsive', 'controlled'],
  ['dominance', 'yielding', 'assertive'],
  ['empathy', 'detached', 'sensitive'],
  ['curiosity', 'familiarity-seeking', 'novelty-seeking'],
  ['drive', 'content', 'ambitious'],
  ['sexual_drive', 'low libido', 'strong libido'],
  ['casual_sex_openness', 'exclusivity-oriented', 'casual-comfortable'],
];

// Bipolar relationship fields: center-zero diverging meter.
const BIPOLAR: Array<{ key: 'affinity' | 'trust' | 'respect'; label: string; neg: string; pos: string }> = [
  { key: 'affinity', label: 'Affinity', neg: 'dislike', pos: 'affection' },
  { key: 'trust', label: 'Trust', neg: 'distrust', pos: 'trust' },
  { key: 'respect', label: 'Respect', neg: 'contempt', pos: 'admiration' },
];

// Unipolar fields: 0..10 fill meter.
const UNIPOLAR: Array<{ key: 'familiarity' | 'fear' | 'attraction'; label: string; hint: [string, string] }> = [
  { key: 'familiarity', label: 'Familiarity', hint: ['stranger', 'knows well'] },
  { key: 'fear', label: 'Fear', hint: ['none', 'strong'] },
  { key: 'attraction', label: 'Attraction', hint: ['none', 'strong'] },
];

export type BioTab = 'personality' | 'relationships' | 'state';

// Button tone for the relationship grid: the strongest of affinity vs
// attraction wins the hue (low affinity red, high affinity green, high
// attraction orange, weak both grey); magnitude 0..10 drives saturation.
export type RelTone = 'neg' | 'pos' | 'warm' | 'neutral';

export function relationshipTone(rel: Pick<Relationship, 'affinity' | 'attraction'>): {
  kind: RelTone;
  strength: number; // 0..1 from the 0..10 magnitude
} {
  const aff = rel.affinity;
  const att = rel.attraction;
  if (Math.abs(aff) >= att && Math.abs(aff) > 2) {
    return { kind: aff > 0 ? 'pos' : 'neg', strength: Math.min(1, Math.abs(aff) / 10) };
  }
  if (att > 2) return { kind: 'warm', strength: Math.min(1, att / 10) };
  return { kind: 'neutral', strength: 0 };
}

const TONE_BASE: Record<RelTone, string> = {
  neg: '#f72585',
  pos: '#06d6a0',
  warm: '#ff9e00',
  neutral: '#3a4158',
};

export function toneBackground(tone: { kind: RelTone; strength: number }): string {
  if (tone.kind === 'neutral') return '#2a3042';
  // Saturation scales with magnitude: faint tint → vivid.
  const pct = Math.round(25 + 65 * tone.strength);
  return `color-mix(in srgb, ${TONE_BASE[tone.kind]} ${pct}%, #262b3d)`;
}

function UniBar({ value }: { value: number }): JSX.Element {
  return (
    <div className="meter">
      <div className="meter-fill" style={{ width: `${value * 10}%` }} />
    </div>
  );
}

function BiBar({ value }: { value: number }): JSX.Element {
  // value -10..10 → left half (red) or right half (green) from center.
  const pct = Math.abs(value) * 5;
  const style =
    value >= 0
      ? { left: '50%', width: `${pct}%`, background: '#06d6a0' }
      : { left: `${50 - pct}%`, width: `${pct}%`, background: '#f72585' };
  return (
    <div className="meter meter-bi">
      <div className="meter-center" />
      <div className="meter-fill-bi" style={style} />
    </div>
  );
}

export default function BioPanel(props: {
  npc: Npc;
  rels: Array<{ other: string; rel: Relationship; mems: Memory[]; longterm: string; urge: UrgeInfo }>;
  tab: BioTab;
  onTab: (t: BioTab) => void;
  sleep: SleepState;
  bedSecs: number; // continuous seconds in bed (0 when up)
  wakeAt: number; // rolled energy target for this nap (0 when up)
  onClose: () => void;
}): JSX.Element {
  const [sel, setSel] = useState(0); // selected relationship in the grid
  const { npc, tab } = props;
  // Untouched strangers (all-zero, no memories) stay hidden until something happens.
  const shown = props.rels.filter((r) => hasHistory(r.rel, r.mems, r.longterm));
  const tabs: Array<[BioTab, string]> = [
    ['personality', 'Personality'],
    ['relationships', 'Relationships'],
    ['state', 'State'],
  ];
  return (
    <div className="bio" onMouseDown={(e) => e.stopPropagation()}>
      <div className="bio-head">
        <div>
          <h3>
            {npc.firstName} {npc.lastName} {npc.savedFavorite ? '★' : ''}
          </h3>
          <div className="bio-sub">
            <span className={npc.origin === 'llm' ? 'origin-llm' : npc.origin === 'human' ? 'origin-human' : 'origin-offline'} title={npc.origin === 'llm' ? 'Personality created by DeepSeek' : npc.origin === 'human' ? 'Hand-made by a human in the character creator' : 'Procedural fallback — the LLM call failed'}>
              {npc.origin === 'llm' ? '✦ LLM-made' : npc.origin === 'human' ? '◈ human-made' : '○ offline-made'}
            </span>
            {' · '}{npc.state.mood}
          </div>
        </div>
        <button className="bio-close" onClick={props.onClose} aria-label="Close bio">
          ✕
        </button>
      </div>
      <div className="bio-tabs">
        {tabs.map(([t, label]) => (
          <button key={t} className={tab === t ? 'bio-tab active' : 'bio-tab'} onClick={() => props.onTab(t)}>
            {label}
            {t === 'relationships' && shown.length > 0 ? ` (${shown.length})` : ''}
          </button>
        ))}
      </div>
      <div className="bio-body">
        {tab === 'personality' && (
          <>
            <div className="trait" title="50/50 wild card at spawn: wired-hot NPCs lean into extremes">
              Temperament: <strong>{npc.neurotic ? 'intense' : 'steady'}</strong>
              <div style={{ color: '#9aa3b8' }}>
                {npc.origin === 'human'
                  ? 'Hand-crafted by a human in the character creator.'
                  : npc.neurotic
                    ? 'The creator was told to push this one toward the poles.'
                    : 'A middle-of-the-road roll of the dice.'}
              </div>
            </div>
            <strong>Nature</strong>
            {NATURE_LABELS.map(([k, low, high]) => (
              <div key={k} style={{ fontSize: 12, margin: '4px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{k}</span>
                  <span style={{ color: '#9aa3b8' }}>{npc.nature[k]}/10</span>
                </div>
                <UniBar value={npc.nature[k]} />
                <div style={{ color: '#9aa3b8' }}>
                  {npc.nature[k] <= 4 ? low : npc.nature[k] >= 7 ? high : 'balanced'}
                </div>
              </div>
            ))}
            <strong>Learned traits</strong>
            {npc.nurture.map((t) => (
              <div className="trait" key={t.trait_key}>
                <strong>{t.trait_key}</strong> ({t.strength}/10)
                <div style={{ color: '#9aa3b8' }}>{t.reason}</div>
              </div>
            ))}
          </>
        )}
        {tab === 'relationships' && (
          <>
            {shown.length === 0 && <div style={{ fontSize: 13 }}>No relationships yet — let them mingle.</div>}
            {shown.length > 0 && (
              <>
                <div className="rel-grid">
                  {shown.map((r, i) => {
                    const tone = relationshipTone(r.rel);
                    return (
                      <button
                        key={r.other}
                        className={i === Math.min(sel, shown.length - 1) ? 'rel-btn sel' : 'rel-btn'}
                        style={{ background: toneBackground(tone) }}
                        title={`${r.other} (affinity ${r.rel.affinity}, attraction ${r.rel.attraction})`}
                        onClick={() => setSel(i)}
                      >
                        {r.other}
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const r = shown[Math.min(sel, shown.length - 1)];
                  return (
                    <div className="rel-card" key={r.other}>
                      <strong>{r.other}</strong>
                      <div className="rel-row">
                        <div className="rel-label">
                          <span>Urge to chat with</span>
                          <span className="pos">{r.urge.value}</span>
                        </div>
                        <UniBar value={r.urge.value / 10} />
                        <div className="rel-hint">
                          like {r.urge.a.toFixed(2)} × recent {r.urge.r.toFixed(2)} × near {r.urge.d.toFixed(2)} × spark {r.urge.t.toFixed(2)}
                        </div>
                      </div>
                {BIPOLAR.map((f) => (
                  <div key={f.key} className="rel-row">
                    <div className="rel-label">
                      <span>{f.label}</span>
                      <span className={r.rel[f.key] > 0 ? 'pos' : r.rel[f.key] < 0 ? 'neg' : 'mut'}>
                        {r.rel[f.key] > 0 ? `+${r.rel[f.key]}` : r.rel[f.key]}
                      </span>
                    </div>
                    <BiBar value={r.rel[f.key]} />
                    <div className="rel-hint">
                      {r.rel[f.key] <= -4 ? f.neg : r.rel[f.key] >= 4 ? f.pos : 'neutral'}
                    </div>
                  </div>
                ))}
                {UNIPOLAR.map((f) => (
                  <div key={f.key} className="rel-row">
                    <div className="rel-label">
                      <span>{f.label}</span>
                      <span className="mut">{r.rel[f.key]}/10</span>
                    </div>
                    <UniBar value={r.rel[f.key]} />
                    <div className="rel-hint">
                      {r.rel[f.key] <= 3 ? f.hint[0] : r.rel[f.key] >= 7 ? f.hint[1] : 'developing'}
                    </div>
                  </div>
                ))}
                {r.mems.slice(0, 5).map((m) => (
                  <div key={m.memoryId} className="mem">
                    [{m.salience}] {m.summary}
                  </div>
                ))}
                {r.longterm && <div className="longterm">“{r.longterm}”</div>}
                    </div>
                  );
                })()}
              </>
            )}
          </>
        )}
        {tab === 'state' && (
          <>
            <div className="rel-row">
              <div className="rel-label">
                <span>Mood</span>
                <span className="pos">{npc.state.mood}</span>
              </div>
            </div>
            <div className="rel-row">
              <div className="rel-label">
                <span>Sleep</span>
                <span className={props.sleep === 'awake' ? 'mut' : 'pos'}>
                  {props.sleep === 'awake' ? 'awake' : props.sleep === 'napping' ? 'napping' : 'deep sleeping'}
                  {props.bedSecs > 0 ? ` (${props.bedSecs}s in bed${props.wakeAt > 0 ? ` · up at ${props.wakeAt}` : ''})` : ''}
                </span>
              </div>
              {props.sleep !== 'awake' && (
                <div className="rel-hint">Woken sleepers talk groggy — the LLM is told.</div>
              )}
            </div>
            <div className="rel-row">
              <div className="rel-label">
                <span>Stress</span>
                <span className="mut">{npc.state.stress}/10</span>
              </div>
              <UniBar value={npc.state.stress} />
            </div>
            <div className="rel-row">
              <div className="rel-label">
                <span>Energy</span>
                <span className="mut">{npc.state.energy}/10</span>
              </div>
              <UniBar value={npc.state.energy} />
              <div className="rel-hint">
                {npc.state.energy >= 7 ? 'seeks company' : npc.state.energy <= 3 ? 'needs space' : 'easy drift'}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
